// CheckoutForm.jsx
import React, { useState,useRef } from 'react';
import { PaymentElement, AddressElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_BASE_URL = process.env.REACT_APP_FRONTEND_BASE_URL;

// Address autocomplete. By default ("automatic") Stripe uses its own Google
// Maps key

const CheckoutForm = ({ onCountryChange, setCompleteAddress, total, shipping, taxes, baseProduct, onPaymentIntentUpdated, createPaymentIntent, isQuoting, defaultEmail, urlEmailError, pricingTier, allAgreed, setAllAgreed, sid, isFreeSid, migratedCust, selectedPlan, onPlanChange }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState(null);
 // const [shippingName, setShippingName] = useState('');
  const [shippingAddress, setShippingAddress] = useState(null);
  const [shippingPhone, setShippingPhone] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [email, setEmail] = useState(defaultEmail || '');
  const [previousAddress, setPreviousAddress] = useState(null);
  const [agreed1, setAgreed1] = useState(false);
  const [agreed2, setAgreed2] = useState(false);
  const [agreed3, setAgreed3] = useState(false);
  const [agreed4, setAgreed4] = useState(false);
  const isMigratedCust = migratedCust === "migrated_cust";
  // Migrated customers: agreements are pre-accepted/not shown, so no checkbox tracking
  // is needed and the pay button stays enabled.
  const allAgreedLocal = isMigratedCust || (agreed1 && agreed2 && agreed3 && agreed4);
  const leadStoredRef = useRef(false);
  const pendingEventsRef = useRef([]);

  // Sync local allAgreed to parent whenever it changes
  React.useEffect(() => {
    if (setAllAgreed) {
      setAllAgreed(allAgreedLocal);
    }
  }, [allAgreedLocal, setAllAgreed]);

  // Track checkbox tick/untick events in LeadGenEvents
  const trackCheckboxEvent = async (checkboxNumber, isChecked) => {
    const emailToUse = email || defaultEmail;
    if (!emailToUse) {
      // No email yet on a free SID page — queue event to be flushed after lead is stored
      if (isFreeSid && !defaultEmail) {
        pendingEventsRef.current.push({ checkboxNumber, isChecked });
      }
      return;
    }
    try {
      await axios.post(`${BASE_URL}/api/lead/event`, {
        email: emailToUse,
        event_type: `checkbox_${checkboxNumber}`,
        event_status: isChecked ? "tick" : "untick",
      });
    } catch (err) {
      console.warn(`⚠️ Failed to track checkbox_${checkboxNumber} event:`, err?.response?.data?.error || err.message);
    }
  };

  // Store lead and flush pending checkbox events when user moves focus away from email input
  const handleEmailBlur = () => {
    if (!isFreeSid || defaultEmail || leadStoredRef.current) return;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

    leadStoredRef.current = true;
    const storeLead = async () => {
      try {
        const resp = await axios.post(`${BASE_URL}/api/lead/store`, {
          email: email,
          sid: sid || null,
        });

        // Flush any pending checkbox events that were queued before email was entered
        const pending = pendingEventsRef.current;
        pendingEventsRef.current = [];
        for (const evt of pending) {
          try {
            await axios.post(`${BASE_URL}/api/lead/event`, {
              email: email,
              event_type: `checkbox_${evt.checkboxNumber}`,
              event_status: evt.isChecked ? "tick" : "untick",
            });
          } catch (err) {
            console.warn(`⚠️ Failed to flush pending checkbox_${evt.checkboxNumber} event:`, err?.response?.data?.error || err.message);
          }
        }

        if (resp.data?.redirectUrl) {
          window.location = resp.data.redirectUrl;
        }
      } catch (err) {
        console.warn("⚠️ Failed to store lead for manually entered email:", err?.response?.data?.error || err.message);
      }
    };
    storeLead();
  };

  // Log when component mounts/remounts with payment details
  React.useEffect(() => {
  }, [baseProduct, shipping, taxes, total]);

  // In deferred mode the new amount reaches Elements (and Apple Pay) through the
  // `amount` option on <Elements>, which react-stripe-js applies with
  // elements.update() — no fetchUpdates() call is needed here.

  const handleAddressChange = (event) => {
      const address = event.value?.address;
      if (event.value?.phone !== undefined) {
        setShippingPhone(event.value.phone || '');
      }
      const country = event.value?.address?.country;
      
      // Always track country changes, even if address is incomplete
      // This ensures shipping cost updates when user changes country dropdown
      if (country && (!previousAddress || previousAddress.country !== country)) {
        onCountryChange(country);
        // Update previous address country to avoid repeated calls
        setPreviousAddress(prev => prev ? { ...prev, country } : { country });
      }
      
      // Only set complete address when all required fields are filled
      if (
        address?.line1 &&
        address?.city &&
        address?.country &&
        (address?.state || address?.country)
      ) {
          setShippingAddress(event.value);
          
          // Check if address fields changed (not just the name)
          const addressChanged = !previousAddress || 
            previousAddress.line1 !== address.line1 ||
            previousAddress.line2 !== address.line2 ||
            previousAddress.city !== address.city ||
            previousAddress.state !== address.state ||
            previousAddress.postal_code !== address.postal_code ||
            previousAddress.country !== address.country;
          
          if (addressChanged) {
            setPreviousAddress(address);
            setCompleteAddress(event.value.address);
          }
      }
  };
  // Report a failed payment against the lead, as before. The PaymentIntent id
  // is sent along whenever the attempt got far enough to have one: the backend
  // re-reads that intent from Stripe to confirm the report really belongs to
  // this checkout instead of taking it on trust. Attempts that fail before an
  // intent exists have none to send, and the backend still accepts those.
  const reportPaymentError = async (error, paymentIntentId) => {
    const emailToUse = email || defaultEmail;
    if (!emailToUse) return;
    try {
      await axios.post(`${BASE_URL}/api/lead/update-status`, {
        email: emailToUse,
        status: "error",
        additionalInfo: error.message || error,
        ...(paymentIntentId ? { paymentIntentId } : {}),
      });
    } catch (leadErr) {
      console.warn("Failed to update lead status:", leadErr?.response?.data?.error || leadErr.message);
    }
  };

  const buildConfirmParams = (returnUrl) => {
    const confirmParams = {
      // Make sure to change this to your payment completion page
      return_url: returnUrl,
    };

    // If we collected an email, include it in billing details and as receipt email
    if (email) {
      confirmParams.payment_method_data = {
        billing_details: { email },
      };
      confirmParams.receipt_email = email;
    }

    // If we have a shipping address collected via the AddressElement, include it
    if (shippingAddress) {
      confirmParams.shipping = {
        address: {
          line1: shippingAddress.line1 || shippingAddress.street || undefined,
          line2: shippingAddress.line2 || undefined,
          city: shippingAddress.city || undefined,
          state: shippingAddress.state || shippingAddress.region || undefined,
          postal_code: shippingAddress.postal_code || shippingAddress.postalCode || undefined,
          country: shippingAddress.country || undefined,
        },
      };
    }

    return confirmParams;
  };

  // Deferred confirmation: validate the form, create the PaymentIntent with the
  // final amount, then confirm it. The PaymentIntent is created here and nowhere
  // else, so no incomplete intent exists until the customer actually pays.
  const submitAndConfirm = async ({ returnUrl, redirect }) => {
    const { error: submitError } = await elements.submit();
    if (submitError) {
      return { error: submitError };
    }

    // Stripe attaches the Address Element's details (including the phone) to the
    // PaymentIntent when confirming, but we also store the number on the intent
    // itself so the webhook can put it on the Shopify order independently.
    const created = await createPaymentIntent({
      phone: shippingPhone || shippingAddress?.phone || '',
      email: email || defaultEmail || '',
    });
    if (!created?.success || !created?.clientSecret) {
      return { error: { message: created?.message || "Could not start the payment. Please try again." } };
    }
    const confirmParams = buildConfirmParams(returnUrl);

    // Carry the intent id out with the result, so a failure can be reported
    // against the intent it actually happened on. /checkout/create-intent
    // returns it; the client secret is `<intent id>_secret_<key>` as a fallback.
    const paymentIntentId =
      created.data?.paymentIntentId || created.clientSecret.split("_secret_")[0];

    const result = await stripe.confirmPayment({
      elements,
      clientSecret: created.clientSecret,
      confirmParams,
      ...(redirect ? { redirect } : {}),
    });

    return { ...result, paymentIntentId };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      console.error(" Stripe or Elements not ready");
      return;
    }

    setErrorMessage(null);
    setIsProcessing(true);
    let error;
    let paymentIntentId;

    try {
      const result = await submitAndConfirm({
        returnUrl: `${FRONTEND_BASE_URL}/success`,
      });
      error = result.error;
      paymentIntentId = result.paymentIntentId;
    } catch (err) {
      error = err;
    } finally {
      setIsProcessing(false);
    }

    // Update lead status on error (e.g. 402 Payment Required)
    if (error) {
      await reportPaymentError(error, paymentIntentId);
      setErrorMessage(error.message);
    } else {
      // The customer will be redirected to the return_url on success
    }
  };

  // Apple Pay confirmation handler to ensure updated amount is used
  // eslint-disable-next-line no-unused-vars
  const handlePaymentElementConfirm = async (event) => {

    if (!stripe || !elements) {
      console.error("Stripe or elements not initialized");
      return { error: { message: "Stripe not initialized" } };
    }

    const returnUrl = `${FRONTEND_BASE_URL}success`;

    try {
      const { error, paymentIntentId } = await submitAndConfirm({ returnUrl, redirect: 'if_required' });

      if (error) {
        console.error(" Apple Pay confirmation error:", error);
        // Update lead status to error
        await reportPaymentError(error, paymentIntentId);
        return { error };
      }
      // On success, redirect manually
      window.location.href = returnUrl;
      return {};
    } catch (err) {
      console.error(" Apple Pay confirmation exception:", err);
      return { error: { message: err.message } };
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {isFreeSid && (
        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="group relative cursor-pointer">
              <input
                type="radio"
                name="plan"
                value="monthly"
                checked={selectedPlan === "monthly"}
                onChange={() => onPlanChange("monthly")}
                className="peer sr-only"
              />
              <div className={`border rounded-xl p-4 md:p-6 transition-all duration-200 h-full ${selectedPlan === "monthly" ? "border-blue-600 ring-2 ring-blue-600 bg-blue-50" : "border-gray-200 bg-white group-hover:border-blue-300"}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-gray-900">Monthly Plan</h3>
                  <div className="flex flex-col items-end">
                    <div className="text-xs font-bold text-gray-400 line-through">$19</div>
                    <div className="text-2xl font-bold text-gray-900">$15</div>
                    <div className="text-sm text-gray-500">/ month</div>
                  </div>
                </div>
                <ul className="space-y-2 mb-4 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Billed monthly</span>
                  </li>
                </ul>
              </div>
            </label>

            <label className="group relative cursor-pointer">
              <input
                type="radio"
                name="plan"
                value="yearly"
                checked={selectedPlan === "yearly"}
                onChange={() => onPlanChange("yearly")}
                className="peer sr-only"
              />
              <div className={`border rounded-xl transition-all duration-200 h-full ${selectedPlan === "yearly" ? "border-blue-600 ring-2 ring-blue-600 bg-blue-50" : "border-gray-200 bg-white group-hover:border-blue-300"}`}>
                {/* Black Friday bar */}
                <div className="bg-black text-yellow-200 text-xs font-bold px-4 md:px-6 py-1 rounded-tl-xl rounded-tr-xl flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1">🎁 Black Friday Deal</span>
                  <span className="flex items-center gap-1">✨ ✨</span>
                </div>
                <div className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-gray-900">Annual Plan</h3>
                  <div className="flex flex-col items-end">
                    <div className="text-xs font-bold text-gray-400 line-through">$180</div>
                    <div className="text-3xl font-bold text-gray-900">$150</div>
                    <div className="text-sm text-gray-500">/ year</div>
                  </div>
                </div>
                <ul className="space-y-2 mb-4 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Billed annually (save $30)</span>
                  </li>
                </ul>
              </div>
            </div>
            </label>
          </div>
          <p className="text-xs text-gray-500 text-center mt-3">
            Select your preferred billing plan before checking out.
          </p>
        </div>
      )}
      <label className="block mb-2">
        <span className="text-sm">Email</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={handleEmailBlur}
          placeholder="you@example.com"
          required
          className={`mt-1 p-2 border rounded w-full ${urlEmailError ? 'border-red-500' : ''}`}
        />
        {urlEmailError && (
          <span className="block mt-1 text-xs text-red-600 font-medium">{urlEmailError}</span>
        )}
      </label>

      <div className="relative z-20 block mb-4" data-checkout="address">
        <span className="block text-sm" style={{ fontWeight: 700, fontSize: '15px' }} >Shipping address</span>
        <div className="mt-1">
          <AddressElement
            options={{
              mode: 'shipping',
              autocomplete:  { mode: 'automatic' },
              fields: {
                phone: 'always',
              },
              validation: {
              phone: {
                required: 'always', // Forces the phone number to be mandatory ("Phone number")
              },
             },
              defaultValues: { address: {
                  country: 'US'
                } },
            }}
             onChange={handleAddressChange}
          />
        </div>
      </div>
      <div className="block mb-2" style={{ fontWeight: 700, fontSize: '15px' }}>
        Payment Detail
      </div>
      <div className="relative z-0" data-checkout="payment">
        <PaymentElement 
          onChange={(event) => {
          }}
          options={{
            wallets: {
              applePay: 'auto'
            }
          }}
        />
      </div>
      {pricingTier === "free" && !isMigratedCust && (
        <div className="mt-6 space-y-3 border-t border-gray-200 pt-4">
          <p className="text-sm font-semibold text-gray-800 mb-3">Agreements</p>
          
          <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={agreed1}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAgreed1(checked);
                  trackCheckboxEvent(1, checked);
                }}
                className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
              <span className="text-xs sm:text-sm text-gray-700 leading-relaxed select-none group-hover:text-gray-900 transition-colors">
                I understand this is a pre-order, and I'm part of Batch 4. My WurQ Gen 2 system is expected to ship between mid and late November.
              </span>
            </label>

          <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={agreed2}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAgreed2(checked);
                  trackCheckboxEvent(2, checked);
                }}
                className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
              <span className="text-xs sm:text-sm text-gray-700 leading-relaxed select-none group-hover:text-gray-900 transition-colors">
                I agree to the initial 6-month membership commitment.<br/>I understand that the sensors are included with my membership and that my plan has an initial 6-month commitment.
              </span>
            </label>

          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={agreed3}
              onChange={(e) => {
                const checked = e.target.checked;
                setAgreed3(checked);
                trackCheckboxEvent(3, checked);
              }}
              className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
            <span className="text-xs sm:text-sm text-gray-700 leading-relaxed select-none group-hover:text-gray-900 transition-colors">
              I agree to receive communication from WurQ.<br/>I agree to receive important emails and messages related to my order, shipment, membership, product updates, and the Build With Us program.
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={agreed4}
              onChange={(e) => {
                const checked = e.target.checked;
                setAgreed4(checked);
                trackCheckboxEvent(4, checked);
              }}
              className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
            <span className="text-xs sm:text-sm text-gray-700 leading-relaxed select-none group-hover:text-gray-900 transition-colors">
               I understand this is an early Build With Us offer.<br/>I’m joining WurQ Gen 2 early and helping shape the product as it continues to improve. I understand that some features may evolve and occasional tech challenges may arise. I’m willing to provide feedback as I use the system, and WurQ will keep releasing updates based on user feedback
            </span>
          </label>
        </div>
      )}

      {errorMessage && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {errorMessage}
        </div>
      )}

      <div className="mt-4">
        <button
          type="submit"
          disabled={!stripe || isProcessing || isQuoting || (pricingTier === "free" && !allAgreedLocal)}
          aria-disabled={!stripe || isProcessing || isQuoting || (pricingTier === "free" && !allAgreedLocal)}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
        >
          {isProcessing ? 'Processing...' : isQuoting ? 'Updating total...' : 'Pay'}
        </button>
      </div>
    </form>
  );
};
export default CheckoutForm;